<template>
    <div>
        <!-- Loading state -->
        <div v-if="loading" class="my-8 flex justify-center">
            <CSpinner />
        </div>
        
        <!-- Related items grid -->
        <div v-else-if="relatedItems.items && relatedItems.items.length > 0" class="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
            <CMasonryCard
                v-for="item in relatedItems.items"
                :key="getItemId(item)"
                :image-link="getImageLink(item, relatedItems.cardType)"
                :hide-image="compact && relatedItems.cardType === EntityType.Job"
                :toasts="getToasts(item, relatedItems.cardType)"
                :toast-type="getToastTypes(item, relatedItems.cardType)"
                :title="getTitle(item, relatedItems.cardType)"
                :subscript="getSubscript(item, relatedItems.cardType)"
                :attributes="getAttributes(item, relatedItems.cardType)"
                :attribute-icon-names="getAttributeIcons(item, relatedItems.cardType)"
                :tags="getTags(item, relatedItems.cardType)"
                :page="getPageRoute(relatedItems.cardType)"
                :page-params="getPageParams(item, relatedItems.cardType)"
                @click="handleItemClick(item, relatedItems.cardType)"
                @tag-click="handleTagClick"
            />
            
            <!-- More items card -->
            <MoreItemsCard
                v-if="moreItemsCard?.shouldShowMoreCard"
                :button-text="moreItemsCard.cardLabel"
                :items-left-label="'items left'"
                :items-left-count="moreItemsCard.length || 0"
                :route="moreItemsCard.redirectTo"
                @click-button="$emit('on-show-more-button-click')"
            />
        </div>
        
        <!-- No items found -->
        <div v-else class="py-8 text-center text-gray-500 dark:text-txt-body">
            {{ $t('relatedItems.noItemsFound', lang || 'sk') }}
        </div>
    </div>
</template>

<script setup lang="ts">
import type { PropType } from 'vue'

import type { RelatedItems } from '#shared/types/RelatedItems'
import { EntityType } from '#shared/types/EntityType'
import type { RouteLocationRaw } from 'vue-router'

// Import components
import CMasonryCard from '@/components/ui/CMasonryCard.vue'
import MoreItemsCard from '@/components/common/MoreItemsCard.vue'

interface ItemsCardType {
    cardLabel: string
    redirectTo: RouteLocationRaw
    length?: number
    shouldShowMoreCard?: boolean
}

defineProps({
    compact: {
        type: Boolean,
        default: false
    },
    lang: {
        type: String,
        default: ''
    },
    loading: {
        type: Boolean,
        default: false
    },
    moreItemsCard: {
        type: Object as PropType<ItemsCardType>,
        default: undefined
    },
    noSeparator: {
        type: Boolean,
        default: false
    },
    relatedItems: {
        type: Object as PropType<RelatedItems>,
        default: () => ({})
    }
})

const emit = defineEmits(['on-show-more-button-click', 'item-click'])

// Helper functions to extract data based on entity type
function getItemId(item: any): string {
    return item.id || item.objectID || item.slug || 'unknown'
}

function getImageLink(item: any, cardType: EntityType): string {
    switch (cardType) {
        case EntityType.Job:
            return item.image || item.coverImg || item.orgImage || item.orgImg || item.orgMeta?.image || ''
        case EntityType.Mentor:
        case EntityType.Volunteer:
            return item.profileImg || ''
        case EntityType.Project:
            return item.coverImg || item.image || item.orgImage || item.orgLogo || item.orgMeta?.image || ''
        case EntityType.Skill:
            return item.coverImg || item.orgImage || item.orgMeta?.image || ''
        case EntityType.Org:
            return item.logo || item.image || ''
        default:
            return ''
    }
}

function getToasts(item: any, cardType: EntityType): string[] {
    const toasts: string[] = []
    
    switch (cardType) {
        case EntityType.Job:
            if (item.remote || item.remoteWork) toasts.push('Remote')
            if (item.isUrgent) toasts.push('Urgent')
            break
        case EntityType.Project:
            if (item.isOnline) toasts.push('Online')
            if (item.isInclusive) toasts.push('Inclusive')
            break
        case EntityType.Skill:
            if (item.isOnline) toasts.push('Online')
            if (item.skillType) toasts.push(item.skillType)
            break
        case EntityType.Mentor:
        case EntityType.Volunteer:
            if (item.isAvailable) toasts.push('Available')
            break
        case EntityType.Org:
            if (item.isVerified) toasts.push('Verified')
            break
    }
    
    return toasts
}

function getToastTypes(item: any, cardType: EntityType): string[] {
    const types: string[] = []
    
    switch (cardType) {
        case EntityType.Job:
            if (item.remote || item.remoteWork) types.push('info')
            if (item.isUrgent) types.push('warning')
            break
        case EntityType.Project:
            if (item.isOnline) types.push('info')
            if (item.isInclusive) types.push('success')
            break
        case EntityType.Skill:
            if (item.isOnline) types.push('info')
            if (item.skillType) types.push('default')
            break
        case EntityType.Mentor:
        case EntityType.Volunteer:
            if (item.isAvailable) types.push('success')
            break
        case EntityType.Org:
            if (item.isVerified) types.push('success')
            break
    }
    
    return types
}

function getTitle(item: any, cardType: EntityType): string {
    switch (cardType) {
        case EntityType.Job:
        case EntityType.Project:
        case EntityType.Skill:
            return item.title || 'Untitled'
        case EntityType.Mentor:
        case EntityType.Volunteer:
            return `${item.firstName || ''} ${item.lastName || ''}`.trim() || 'Anonymous'
        case EntityType.Org:
            return item.name || item.title || 'Organization'
        default:
            return 'Item'
    }
}

function getSubscript(item: any, cardType: EntityType): string {
    switch (cardType) {
        case EntityType.Job:
            return item.orgName || item.orgMeta?.title || item.orgMeta?.name || ''
        case EntityType.Mentor:
        case EntityType.Volunteer:
            return item.currentJob || item.jobTitle || ''
        case EntityType.Project:
            return item.orgName || item.orgMeta?.title || item.organization?.name || ''
        case EntityType.Skill:
            return item.orgName || item.orgMeta?.title || item.skillTypeName || item.category || ''
        case EntityType.Org:
            return item.orgType || item.type || ''
        default:
            return ''
    }
}

function getAttributes(item: any, cardType: EntityType): string[] {
    const attributes: string[] = []
    
    switch (cardType) {
        case EntityType.Job:
            if (item.location || item.city) {
                attributes.push(item.location || item.city)
            }
            if (item.salary) {
                attributes.push(item.salary)
            }
            break
        case EntityType.Mentor:
        case EntityType.Volunteer:
            if (item.location || item.city) {
                attributes.push(item.location || item.city)
            }
            break
        case EntityType.Project:
            if (item.city && !item.isOnline) {
                attributes.push(item.city)
            }
            if (item.startDate) {
                attributes.push(new Date(item.startDate).toLocaleDateString())
            }
            break
        case EntityType.Skill:
            if (item.city && !item.isOnline) {
                attributes.push(item.city)
            }
            if (item.startDate) {
                attributes.push(new Date(item.startDate).toLocaleDateString())
            }
            break
        case EntityType.Org:
            if (item.location || item.city) {
                attributes.push(item.location || item.city)
            }
            if (item.activeOpportunitiesCount) {
                attributes.push(`${item.activeOpportunitiesCount} opportunities`)
            }
            break
    }
    
    return attributes
}

function getAttributeIcons(item: any, cardType: EntityType): string[] {
    const icons: string[] = []
    
    switch (cardType) {
        case EntityType.Job:
            if (item.location || item.city) {
                icons.push('location-outline')
            }
            if (item.salary) {
                icons.push('money-48')
            }
            break
        case EntityType.Mentor:
        case EntityType.Volunteer:
            if (item.location || item.city) {
                icons.push('location-outline')
            }
            break
        case EntityType.Project:
            if (item.city && !item.isOnline) {
                icons.push('location-outline')
            }
            if (item.startDate) {
                icons.push('calendar')
            }
            break
        case EntityType.Skill:
            if (item.city && !item.isOnline) {
                icons.push('location-outline')
            }
            if (item.startDate) {
                icons.push('calendar')
            }
            break
        case EntityType.Org:
            if (item.location || item.city) {
                icons.push('location-outline')
            }
            if (item.activeOpportunitiesCount) {
                icons.push('suitcase-48')
            }
            break
    }
    
    return icons
}

function getTags(item: any, cardType: EntityType): string[] {
    switch (cardType) {
        case EntityType.Job:
            return item.hashtag || item.tags || item.skills || []
        case EntityType.Mentor:
        case EntityType.Volunteer:
            return item.interests || item.skills || item.hashtag || []
        case EntityType.Project:
        case EntityType.Skill:
            return item.hashtag || item.tags || item.skills || []
        case EntityType.Org:
            return item.hashtag || item.tags || []
        default:
            return []
    }
}

function getPageRoute(cardType: EntityType): string {
    switch (cardType) {
        case EntityType.Job:
            return 'praca-slug'
        case EntityType.Mentor:
            return 'mentor-id'
        case EntityType.Volunteer:
            return 'dobrovolnictvo-dobrovolnici-id'
        case EntityType.Project:
            return 'dobrovolnictvo-dobrovolnicke-projekty-id'
        case EntityType.Skill:
            return 'vzdelavanie-slug'
        case EntityType.Org:
            return 'orgs-id'
        default:
            return ''
    }
}

function getPageParams(item: any, cardType: EntityType): Record<string, any> {
    const value = item.slug || item.id
    switch (cardType) {
        case EntityType.Job:
        case EntityType.Skill:
            return { slug: value }
        default:
            return { id: value }
    }
}

function handleItemClick(item: any, cardType: EntityType) {
    emit('item-click', { id: getItemId(item), type: cardType })
}

function handleTagClick(tag: string) {
    // Handle tag click - could navigate to filtered view
    console.log('Tag clicked:', tag)
}
</script>
